import { Suspense, useLayoutEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { updateEventsPositions, getEventById, getEventsForDate } from "../../../store/events";
// Only the layout maths; the components themselves load on demand.
import { cloneLayoutItem, compact, correctBounds, setTransform } from "react-grid-layout/build/utils";
import { calcGridItemPosition } from "react-grid-layout/build/calculateUtils";
import { Responsive } from "./gridLayout";
import { EventItem } from '../Event';
import dayjs from "dayjs";
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './GridAsTile.scss'
import { updateEventsPositionsAPI } from '../../../api/eventAPI';

const COLS = 2;
const MARGIN = [1, 1];
const COLS_BY_BREAKPOINT = { lg: COLS, md: COLS, sm: COLS, xs: COLS, xxs: COLS };

// Where react-grid-layout would put each event: inside the columns, with overlapping events pushed down.
const gridLayout = (data) => compact(
    correctBounds(data.map((item) => cloneLayoutItem({ ...item, i: String(item.i) })), { cols: COLS }),
    null,
    COLS,
);

// A day's events. Usually they're plain positioned boxes, laid out with react-grid-layout's own maths;
// the day under the pointer while Ctrl is held gets a real react-grid-layout so its events can be moved.
function GridAsTile(props) {
    const DEFAULT_MAX_ROWS = 2;
    const dispatch = useDispatch();
    const date = dayjs(props.date).format('YYYY-MM-DD');
    const eventsForDate = props.events
    const maxRows = props.maxRows || DEFAULT_MAX_ROWS;

    const eventsIds = useSelector(state => getEventsForDate(state, date));
    const events = useSelector(state => eventsIds.map((id) => getEventById(state, id)), shallowEqual);
    const active = useSelector(state => state.calendar.gridDragDay === date);
    const [dragging, setDragging] = useState(false);
    const [saving, setSaving] = useState(false);
    const data = events.map((event) => ({
        i: event.id,
        x: event.pos_x,
        y: event.pos_y,
        w: event.width / 50,
        h: event.height / 50,
        id: event.id,
    }))
    const calculateRowHeight = () => {
        if (!props.maxRows) return DEFAULT_MAX_ROWS
        const uniqueYValues = new Set();
        data.forEach((item) => {
            uniqueYValues.add(item.y);
        });
        if (uniqueYValues.has(3)) return 4
        if (uniqueYValues.has(2)) return 3
        const totalEventsHeight = Array.from(uniqueYValues).reduce((acc, y) => {
            const eventsWithSameY = data.filter((item) => item.y === y);

            if (eventsWithSameY.length > 0) {

                return acc + eventsWithSameY[0].h;
            }
            return acc;
        }, 0);




        return Math.min(Math.max(totalEventsHeight, 2), 4)
    };
    const rowHeight = props.tileHeight / calculateRowHeight();

    // The box the events are laid out in, measured before the first paint; until then nothing is drawn.
    const containerRef = useRef(null);
    const [box, setBox] = useState(null);
    const interactive = (active || dragging || saving) && box !== null;

    useLayoutEffect(() => {
        if (interactive) return undefined;
        const el = containerRef.current;
        const measure = () => {
            const width = el.getBoundingClientRect().width;
            const height = el.offsetHeight;
            setBox(size => (size && size.width === width && size.height === height ? size : { width, height }));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [interactive]);

    const positionParams = box && {
        cols: COLS, margin: MARGIN, containerPadding: MARGIN, containerWidth: box.width, maxRows: Infinity, rowHeight,
    };
    const positions = box && gridLayout(data).map((item) => calcGridItemPosition(positionParams, item.x, item.y, item.w, item.h));

    // Events that start below the bottom edge are hidden; the day shows how many.
    const hiddenEvents = positions ? positions.filter((position) => position.top >= box.height).length : null;
    const { setExtraEvents } = props;
    useLayoutEffect(() => {
        if (hiddenEvents !== null) setExtraEvents(hiddenEvents);
    }, [hiddenEvents, setExtraEvents]);

    const onDragStop = (layout) => {
        setDragging(false);
        setSaving(true);
        let events = [];
        layout.forEach((item) => {
            const id = Number(item.i);
            if (isNaN(id)) return;
            events.push({
                id: id,
                pos_x: item.x,
                pos_y: item.y,
                width: item.w * 50,
                height: item.h * 50,
            });
        })

        updateEventsPositionsAPI(events).then(() => {
            dispatch(updateEventsPositions(events));
        }).catch((error) => {
            console.log(error);
        }).finally(() => setSaving(false));
    }

    const isDraggable = interactive && active && !saving;
    const style = { width: '100%', height: '100%', zIndex: 2 };

    const staticGrid = (
        <div ref={containerRef} className="react-grid-layout layout overflow-hidden" style={style}>
            {positions && data.map((item, i) => (
                <div
                    key={item.id}
                    className="react-grid-item no-transitions grab"
                    style={{ userSelect: 'none', ...setTransform(positions[i]) }}
                >
                    <EventItem
                        id={item.id}
                        eventsInDateCount={eventsForDate.length}
                        maxRows={maxRows}
                        isDragging={true}
                        cellWidth={positions[i].width}
                        cellHeight={positions[i].height}
                    />
                </div>
            ))}
        </div>
    );

    if (interactive) {
        return (
            <Suspense fallback={staticGrid}>
                <Responsive
                    className="layout overflow-hidden"
                    width={box.width}
                    cols={COLS_BY_BREAKPOINT}
                    isDraggable={isDraggable}
                    // As in the day window: only within the day's four cells, and never onto one another.
                    maxRows={2}
                    preventCollision
                    onDragStart={() => setDragging(true)}
                    onDragStop={onDragStop}
                    margin={MARGIN}
                    rowHeight={rowHeight}
                    isResizable={false}
                    compactType={null}
                    style={style}
                    resizeHandle={<div></div>}
                >
                    {data.map((item, i) => (
                        <div
                            key={item.id}
                            data-grid={item}
                            className="no-transitions grab"
                            style={{ userSelect: 'none' }}
                        >
                            <EventItem
                                key={item.id}
                                id={item.id}
                                eventsInDateCount={eventsForDate.length}
                                maxRows={maxRows}
                                isDragging={!isDraggable}
                                cellWidth={positions?.[i]?.width}
                                cellHeight={positions?.[i]?.height}
                            />
                        </div>
                    ))}
                </Responsive>
            </Suspense>
        );
    }

    return staticGrid;
}

export default GridAsTile;
